import { Injectable } from '@angular/core';
import {
  CentralSocketService,
  SharedUserService,
} from '@g99-product-web-ui/shared-service';
import { Observable, Subject, catchError, of, tap } from 'rxjs';

/**
 * In-app notification events over the central WebSocket.
 *
 * Subscribes /user/{userId}/queue/messages and re-emits every event on
 * events$. The payload schema is NOT parsed for display — consumers treat
 * an event purely as a trigger to refetch notification state over REST
 * (see NotificationFacade). This keeps the UI safe against backend payload
 * changes.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationSocketService {
  /**
   * One emission per in-app notification socket event (raw body, unknown shape).
   */
  public readonly events$ = new Subject<unknown>();

  private _subscriptionId: string | null = null;

  constructor(
    private readonly _centralSocketService: CentralSocketService,
    private readonly _sharedUserService: SharedUserService
  ) {}

  public isEnabled(): boolean {
    return this._centralSocketService.isEnabled();
  }

  public isConnected(): boolean {
    return (
      this._centralSocketService.isConnected() && this._subscriptionId !== null
    );
  }

  /**
   * Connects the central socket (idempotent) and subscribes the user topic.
   * Never errors — connection failures are logged and swallowed so the
   * notification facade keeps working on its polling fallback.
   */
  public start(): Observable<void> {
    if (!this.isEnabled()) {
      return of(void 0);
    }

    const userId = this._sharedUserService.getCurrentUserId();
    if (!userId) {
      return of(void 0);
    }

    return this._centralSocketService.connect().pipe(
      tap(() => {
        this.stop(); // replace any previous subscription
        this._subscriptionId = this._centralSocketService.subscribeTopic(
          `/user/${userId}/queue/messages`,
          (message) => this.events$.next(message.body)
        );
      }),
      catchError((error) => {
        console.error('[NotificationSocket] connection failed:', error);
        return of(void 0);
      })
    );
  }

  /**
   * Unsubscribes the user topic. The shared central connection stays alive.
   */
  public stop(): void {
    if (this._subscriptionId) {
      this._centralSocketService.unsubscribeTopic(this._subscriptionId);
      this._subscriptionId = null;
    }
  }
}
